import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { CtaButton } from "@/components/CtaButton";
import CoinIcon from './ui/CoinIcon';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Static demo steps
const DEMO_STEPS = [
  {
    title: "Connect Your Token",
    description: "Easily connect your Solana token by providing the contract address",
    icon: "🔌",
    color: "#9945FF"
  },
  {
    title: "Configure Settings",
    description: "Set your desired volume levels and trading patterns for realistic activity",
    icon: "⚙️",
    color: "#14F195"
  },
  {
    title: "Start Volume Bot",
    description: "Our AI-powered bots start generating volume across multiple DEXs",
    icon: "🚀",
    color: "#03E1FF"
  },
  {
    title: "Track Progress",
    description: "Watch your token climb the trending charts in real-time",
    icon: "📈",
    color: "#DC1FFF"
  }
];

const SHOWCASE_IMAGES = [
  '/images/showcase1',
  '/images/showcase2',
  '/images/showcase3',
  '/images/showcase4',
  '/images/showcase5',
  '/images/showcase6',
];

export function SolanaDemoShowcase() {
  const [current, setCurrent] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % SHOWCASE_IMAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 bg-[#070710] relative">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="mb-2">
            <span className="inline-block px-3 py-1 bg-[#14F195]/10 text-[#14F195] text-sm rounded-full border border-[#14F195]/20 mb-3">#1 Solana Volume Bot</span>
          </div>
          <h2 className="text-5xl font-bold mb-6 text-white">
            Boost Your Token's <span className="text-[#14F195]">Visibility</span>
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-lg mb-8">
            Increase your Solana token's discoverability through intelligent volume distribution across multiple DEXs.
          </p>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-10 items-center">
          {/* Left side - Static Dashboard Preview */}
          <div className="w-full lg:w-3/5">
            <div className="relative rounded-xl overflow-hidden border border-gray-800">
              <div className="relative aspect-video bg-[#0D0D15]">
                {/* Showcase image slider */}
                <picture>
                  <source srcSet={`${SHOWCASE_IMAGES[current]}.avif`} type="image/avif" />
                  <source srcSet={`${SHOWCASE_IMAGES[current]}.webp`} type="image/webp" />
                  <img
                    src={`${SHOWCASE_IMAGES[current]}.svg`}
                    alt={`Showcase step ${current + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
                    style={{ zIndex: 1 }}
                  />
                </picture>
                <div className="absolute inset-0 bg-black/70 z-10" />
                {/* Slider dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                  {SHOWCASE_IMAGES.map((_, idx) => (
                    <button
                      key={idx}
                      className={`w-3 h-3 rounded-full border-2 ${current === idx ? 'bg-[#14F195] border-[#14F195]' : 'bg-gray-700 border-gray-500'} transition-all`}
                      onClick={() => setCurrent(idx)}
                      aria-label={`Showcase slide ${idx + 1}`}
                    />
                  ))}
                </div>
                {/* Simple CTA overlay */}
                <div className="absolute inset-0 flex items-center justify-center z-30">
                  <CtaButton
                    variant="primary"
                    size="lg" 
                    className="px-8 py-4 rounded-xl text-black font-bold text-lg bg-gradient-to-r from-[#14F195] to-[#9945FF] shadow-xl"
                  >
                    {t('home.ctaStartFreeTrial', 'Start Your Free Trial')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </CtaButton>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - How it works steps */}
          <div className="w-full lg:w-2/5">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white mb-6">{t('landing.seeHowItWorks', 'How It Works')}</h3>
              
              {DEMO_STEPS.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl bg-[#14141E]">
                    {step.icon}
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{step.title}</h4>
                    <p className="text-gray-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}